import React from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { AlertDialog } from "components/AlertDialog/AlertDialog";
import { ReportMetadata, ReportRow, ReportsService } from "gen/clients/llts";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { createExporter } from "./DataExporter";
import { ReportExportType } from "./ExportTypes";

interface Props {
  report: ReportMetadata;
  exportType: ReportExportType;
  filters: string | undefined;
}

const PAGE_SIZE = 1000;

const ExportReportData: React.FC<Props> = ({ report, exportType, filters }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [progress, setProgress] = React.useState({ current: 0, total: 0 });
  const cancelledRef = React.useRef(false);

  const downloadFile = React.useCallback(
    (allItems: ReportRow[], totalRows: number) => {
      const exporter = createExporter(exportType);
      const { blob, filename } = exporter.exportData(report, {
        items: allItems,
        totalRows
      });

      const a = document.createElement("a");
      a.setAttribute("style", "display:none;");
      document.body.appendChild(a);

      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      document.body.removeChild(a);
    },
    [exportType, report]
  );

  const onExport = React.useCallback(async () => {
    setIsLoading(true);
    setProgress({ current: 0, total: 0 });
    cancelledRef.current = false;

    try {
      // Fetch first page to get total count
      const firstPageData = await ReportsService.getReportRows({
        reportId: report.id,
        filters,
        page: 1,
        limit: PAGE_SIZE
      });

      if (cancelledRef.current) {
        return;
      }

      const totalRows = firstPageData.totalRows ?? firstPageData.items.length;
      const totalPages = Math.ceil(totalRows / PAGE_SIZE);
      const allItems: ReportRow[] = [...firstPageData.items];

      setProgress({ current: 1, total: totalPages });

      // Fetch remaining pages if there are more
      for (let page = 2; page <= totalPages; page++) {
        if (cancelledRef.current) {
          return;
        }

        // eslint-disable-next-line no-await-in-loop
        const pageData = await ReportsService.getReportRows({
          reportId: report.id,
          filters,
          page,
          limit: PAGE_SIZE
        });

        if (cancelledRef.current) {
          return;
        }

        allItems.push(...pageData.items);
        setProgress({ current: page, total: totalPages });
      }

      if (!cancelledRef.current) {
        downloadFile(allItems, totalRows);
      }
    } finally {
      setIsLoading(false);
    }
  }, [report.id, filters, downloadFile]);

  const onCancel = React.useCallback(() => {
    cancelledRef.current = true;
    setIsLoading(false);
  }, []);

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <>
      <Button startIcon={<SaveAltIcon />} onClick={onExport} disabled={isLoading}>
        {t("reports.exportData.button.title")}
      </Button>
      {isLoading && (
        <AlertDialog title={t("reports.exportData.alert.title")} buttonText={t("common.cancel")} onClose={onCancel}>
          <Stack spacing={1}>
            <Box>{t("reports.exportData.alert.text")}</Box>
            <Box sx={{ width: "100%" }}>
              <LinearProgress variant="determinate" value={progressPercent} />
            </Box>
            {progress.total > 1 && (
              <Typography variant="body2" color="text.secondary" align="center">
                {t("reports.exportData.alert.progress", { percent: progressPercent })}
              </Typography>
            )}
          </Stack>
        </AlertDialog>
      )}
    </>
  );
};

export { ExportReportData };
